import { createBrowserRouter, redirect } from "react-router";

import { RootLayout } from "@/app/layouts/RootLayout";
import {
  AuthenticatedRouteBoundary,
  OnboardingRouteBoundary,
  PublicAuthRouteBoundary,
  RootEntryRouteBoundary,
} from "@/app/routes/RouteAccessBoundary";
import { RouteLoadingFallback } from "@/app/routes/RouteLoadingFallback";
import { RouteErrorBoundary } from "@/app/routes/RouteErrorBoundary";
import {
  desktopShellRoutes,
  openMyFilesDrawerSearch,
} from "@/features/desktop-shell/desktopShellData";

export const router = createBrowserRouter([
  {
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoadingFallback,
    children: [
      {
        Component: RootEntryRouteBoundary,
        children: [
          {
            path: "/",
            lazy: async () => {
              const { HomePage } = await import("@/pages/home/HomePage");
              return { Component: HomePage };
            },
          },
        ],
      },
      {
        path: "/join",
        lazy: async () => {
          const { TeamInvitationPage } = await import(
            "@/pages/team-invitation/TeamInvitationPage"
          );
          return { Component: TeamInvitationPage };
        },
      },
      {
        Component: PublicAuthRouteBoundary,
        children: [
          {
            path: "/login",
            lazy: async () => {
              const { LoginPage } = await import("@/pages/login/LoginPage");
              return { Component: LoginPage };
            },
          },
          {
            path: "/register",
            lazy: async () => {
              const { RegisterPage } = await import(
                "@/pages/register/RegisterPage"
              );
              return { Component: RegisterPage };
            },
          },
          {
            path: "/forgot-password",
            lazy: async () => {
              const { ForgotPasswordPage } = await import(
                "@/pages/forgot-password/ForgotPasswordPage"
              );
              return { Component: ForgotPasswordPage };
            },
          },
        ],
      },
      {
        Component: OnboardingRouteBoundary,
        children: [
          {
            path: "/onboarding/welcome",
            lazy: async () => {
              const { OnboardingWelcomePage } = await import(
                "@/pages/onboarding-welcome/OnboardingWelcomePage"
              );
              return { Component: OnboardingWelcomePage };
            },
          },
        ],
      },
      {
        path: "/desktop-shell-preview",
        lazy: async () => {
          const { DesktopShellPreviewPage } = await import(
            "@/pages/desktop-shell-preview/DesktopShellPreviewPage"
          );
          return { Component: DesktopShellPreviewPage };
        },
      },
      {
        path: "/merchant/integrations/tiktok_shop/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="tiktok_shop" />,
          };
        },
      },
      {
        path: "/merchant/integrations/shoplazza/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="shoplazza" />,
          };
        },
      },
      {
        path: "/merchant/integrations/shopify/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="shopify" />,
          };
        },
      },
      {
        path: "/merchant/integrations/shopee/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="shopee" />,
          };
        },
      },
      {
        path: "/amazon/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="amazon_sp" />,
          };
        },
      },
      {
        path: "/amazon/oauth/login",
        lazy: async () => {
          const { AmazonSpOAuthLoginPage } = await import(
            "@/pages/store-oauth-callback/AmazonSpOAuthLoginPage"
          );
          return { Component: AmazonSpOAuthLoginPage };
        },
      },
      {
        path: "/merchant/integrations/tiktok_business/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => (
              <StoreOAuthCallbackPage provider="tiktok_business" />
            ),
          };
        },
      },
      {
        path: "/merchant/integrations/amazon_ads/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="amazon_ads" />,
          };
        },
      },
      {
        path: "/merchant/integrations/google_ads/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="google_ads" />,
          };
        },
      },
      {
        path: "/merchant/integrations/alibaba_1688/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="alibaba_1688" />,
          };
        },
      },
      {
        path: "/merchant/integrations/gmail/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="gmail" />,
          };
        },
      },
      {
        path: "/merchant/integrations/meta/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="shared_meta" />,
          };
        },
      },
      {
        path: "/reddit/oauth/callback",
        lazy: async () => {
          const { StoreOAuthCallbackPage } = await import(
            "@/pages/store-oauth-callback/StoreOAuthCallbackPage"
          );
          return {
            Component: () => <StoreOAuthCallbackPage provider="reddit_ads" />,
          };
        },
      },
      {
        Component: AuthenticatedRouteBoundary,
        children: [
          {
            Component: RootLayout,
            children: [
              {
                path: "workspace",
                lazy: async () => {
                  const { WorkspacePage } = await import(
                    "@/pages/workspace/WorkspacePage"
                  );
                  return { Component: WorkspacePage };
                },
              },
              {
                path: "workspace/conversations/:conversationId",
                lazy: async () => {
                  const { WorkspacePage } = await import(
                    "@/pages/workspace/WorkspacePage"
                  );
                  return { Component: WorkspacePage };
                },
              },
              {
                path: "experience-strategies",
                lazy: async () => {
                  const { ExperienceStrategiesPage } = await import(
                    "@/pages/experience-strategies/ExperienceStrategiesPage"
                  );
                  return { Component: ExperienceStrategiesPage };
                },
              },
              {
                path: "workspace/conversations/:conversationId/creative-media/:jobId",
                lazy: async () => {
                  const { CreativeMediaCanvasPage } = await import(
                    "@/pages/creative-media-canvas/CreativeMediaCanvasPage"
                  );
                  return { Component: CreativeMediaCanvasPage };
                },
              },
              {
                path: "chat-history",
                lazy: async () => {
                  const { ChatHistoryPage } = await import(
                    "@/pages/chat-history/ChatHistoryPage"
                  );
                  return { Component: ChatHistoryPage };
                },
              },
              {
                path: "foundation",
                lazy: async () => {
                  const { FoundationPage } = await import(
                    "@/pages/foundation/FoundationPage"
                  );
                  return { Component: FoundationPage };
                },
              },
              {
                path: "my-files",
                Component: () => null,
                loader: ({ request }) => {
                  const requestUrl = new URL(request.url);
                  return redirect(
                    `${desktopShellRoutes.home.path}${openMyFilesDrawerSearch(
                      requestUrl.search,
                      { preserveResource: true },
                    )}`,
                  );
                },
              },
              {
                path: "notifications",
                lazy: async () => {
                  const { NotificationsPage } = await import(
                    "@/pages/notifications/NotificationsPage"
                  );
                  return { Component: NotificationsPage };
                },
              },
              {
                path: "settings/account",
                lazy: async () => {
                  const { AccountSettingsPage } = await import(
                    "@/pages/settings-account/AccountSettingsPage"
                  );
                  return { Component: AccountSettingsPage };
                },
              },
              {
                path: "settings/notifications",
                lazy: async () => {
                  const { NotificationSettingsPage } = await import(
                    "@/pages/settings-notifications/NotificationSettingsPage"
                  );
                  return { Component: NotificationSettingsPage };
                },
              },
              {
                path: "settings/billing",
                lazy: async () => {
                  const { BillingSettingsPage } = await import(
                    "@/pages/settings-billing/BillingSettingsPage"
                  );
                  return { Component: BillingSettingsPage };
                },
              },
              {
                path: "settings/support",
                lazy: async () => {
                  const { SupportSettingsPage } = await import(
                    "@/pages/settings-support/SupportSettingsPage"
                  );
                  return { Component: SupportSettingsPage };
                },
              },
              {
                path: "settings/connections",
                lazy: async () => {
                  const { ConnectionSettingsPage } = await import(
                    "@/pages/settings-connections/ConnectionSettingsPage"
                  );
                  return { Component: ConnectionSettingsPage };
                },
              },
              {
                path: "settings/stores",
                lazy: async () => {
                  const { StoreSettingsPage } = await import(
                    "@/pages/settings-stores/StoreSettingsPage"
                  );
                  return { Component: StoreSettingsPage };
                },
              },
              {
                path: "settings/team",
                lazy: async () => {
                  const { TeamSettingsPage } = await import(
                    "@/pages/settings-team/TeamSettingsPage"
                  );
                  return { Component: TeamSettingsPage };
                },
              },
              {
                path: "settings/ads",
                lazy: async () => {
                  const { AdvertisingAccountSettingsPage } = await import(
                    "@/pages/settings-ads/AdvertisingAccountSettingsPage"
                  );
                  return { Component: AdvertisingAccountSettingsPage };
                },
              },
              {
                path: "settings/tasks",
                lazy: async () => {
                  const { ScheduledTaskSettingsPage } = await import(
                    "@/pages/settings-tasks/ScheduledTaskSettingsPage"
                  );
                  return { Component: ScheduledTaskSettingsPage };
                },
              },
              {
                path: "*",
                lazy: async () => {
                  const { NotFoundPage } = await import(
                    "@/pages/not-found/NotFoundPage"
                  );
                  return { Component: NotFoundPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },
]);
